/**
 * Censo de recursos WebGL do realm atual (janela ou iframe).
 *
 * Motivacao: a galeria 3D da PDP monta, no MOBILE, DOIS iframes de viewer vivos
 * ao mesmo tempo para o mesmo produto — o overlay persistente (que nunca pode
 * receber display:none, senao o canvas colapsa para 0x0) e o iframe do modal de
 * tela cheia (que de proposito NAO reaproveita o overlay: mover um iframe
 * cross-origin com WebGL vivo entre pais disputa com o ciclo de resize do canvas
 * e produzia "Missing canvas"). Abrir o AR acrescenta um <model-viewer>, que traz
 * o SEU proprio contexto. Ou seja: ate tres contextos e tres copias de geometria/
 * textura na GPU para um unico produto.
 *
 * Isso e uma HIPOTESE de pressao de memoria no iOS, nao um fato medido — e nao
 * da para medir sem aparelho. Este modulo existe para transformar a hipotese em
 * numero: em sandbox/dev, `window.__turbineWebglCensus.snapshot()` diz, POR
 * REALM, quantos renderers estao vivos, quanta geometria/textura cada um segura,
 * e quantos canvas/iframes/<model-viewer> existem no documento.
 *
 * Como ler no aparelho (Safari/Chrome remoto): selecione o frame no seletor de
 * contexto do console e rode `__turbineWebglCensus.snapshot()`. O campo `realm`
 * diz qual frame respondeu (overlay x tela cheia). Cada realm responde por si —
 * sao origens/realms separados, nao ha soma automatica.
 */
import type * as THREE from 'three';

interface RendererEntry {
  label: string;
  renderer: THREE.WebGLRenderer;
  createdAt: number;
}

export interface WebglCensusSnapshot {
  realm: string;
  href: string;
  isIframe: boolean;
  /** Renderers Three vivos NESTE realm (criados e ainda nao dispostos). */
  renderers: Array<{
    label: string;
    ageMs: number;
    canvas: { width: number; height: number; cssWidth: number; cssHeight: number };
    contextLost: boolean;
    memory: { geometries: number; textures: number };
    programs: number;
    calls: number;
  }>;
  /** Criados x dispostos desde o load — a diferenca denuncia renderer vazado. */
  renderersCreated: number;
  renderersDisposed: number;
  /** Elementos no documento deste realm. */
  canvasesInDocument: number;
  iframesInDocument: number;
  modelViewersInDocument: number;
  /** Eventos de contexto WebGL perdido/restaurado observados neste realm. */
  contextLostEvents: number;
  contextRestoredEvents: number;
}

const entries = new Set<RendererEntry>();
let created = 0;
let disposed = 0;
let contextLostEvents = 0;
let contextRestoredEvents = 0;

const realmName = (): string => {
  try {
    if (window.parent === window) return 'top';
    const params = new URLSearchParams(window.location.search);
    return params.get('mediaOnly') === 'true' ? 'iframe:mediaOnly' : 'iframe:customizer';
  } catch {
    return 'unknown';
  }
};

export function webglCensusSnapshot(): WebglCensusSnapshot {
  const now = performance.now();
  const doc = typeof document !== 'undefined' ? document : null;
  return {
    realm: realmName(),
    href: typeof location !== 'undefined' ? location.href : '',
    isIframe: typeof window !== 'undefined' && window.parent !== window,
    renderers: Array.from(entries).map(({ label, renderer, createdAt }) => {
      const canvas = renderer.domElement;
      let contextLost = false;
      try { contextLost = renderer.getContext().isContextLost(); } catch { contextLost = true; }
      return {
        label,
        ageMs: Math.round(now - createdAt),
        canvas: {
          width: canvas.width,
          height: canvas.height,
          cssWidth: canvas.clientWidth,
          cssHeight: canvas.clientHeight,
        },
        contextLost,
        memory: { geometries: renderer.info.memory.geometries, textures: renderer.info.memory.textures },
        programs: renderer.info.programs?.length ?? 0,
        calls: renderer.info.render.calls,
      };
    }),
    renderersCreated: created,
    renderersDisposed: disposed,
    canvasesInDocument: doc ? doc.getElementsByTagName('canvas').length : 0,
    iframesInDocument: doc ? doc.getElementsByTagName('iframe').length : 0,
    modelViewersInDocument: doc ? doc.getElementsByTagName('model-viewer').length : 0,
    contextLostEvents,
    contextRestoredEvents,
  };
}

let installed = false;
function installGlobal(): void {
  if (installed) return;
  installed = true;
  try {
    (window as unknown as { __turbineWebglCensus?: unknown }).__turbineWebglCensus = {
      snapshot: webglCensusSnapshot,
      get realm() { return realmName(); },
      get renderersAlive() { return entries.size; },
    };
  } catch { /* noop */ }
}

/**
 * Registra um renderer no censo. Devolve a funcao de baixa — chame no dispose do
 * renderer (o par criado/disposto e justamente o que denuncia contexto vazado
 * entre trocas de variante).
 *
 * Barato o suficiente para rodar sempre; o objeto global so e instalado em
 * dev/sandbox, entao a loja em producao nao ganha superficie de debug nova.
 */
export function registerRenderer(renderer: THREE.WebGLRenderer, label: string): () => void {
  const entry: RendererEntry = { label, renderer, createdAt: performance.now() };
  entries.add(entry);
  created++;

  const canvas = renderer.domElement;
  const onLost = () => { contextLostEvents++; };
  const onRestored = () => { contextRestoredEvents++; };
  canvas.addEventListener('webglcontextlost', onLost);
  canvas.addEventListener('webglcontextrestored', onRestored);

  try {
    const isSandbox = /sandbox/.test(window.location.hostname);
    if (import.meta.env.DEV || isSandbox) installGlobal();
  } catch { /* noop */ }

  return () => {
    if (!entries.delete(entry)) return; // idempotente
    disposed++;
    canvas.removeEventListener('webglcontextlost', onLost);
    canvas.removeEventListener('webglcontextrestored', onRestored);
  };
}
